import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/upload";
import { useAcademicYear } from "@/lib/academicYear";
import BrandMark from "@/components/BrandMark";
import { LogOut, CalendarClock } from "lucide-react";

export default function MagicNav({ items }) {
  const { logout, user, school } = useAuth();
  const navigate = useNavigate();
  const isSchool = user?.role === "school_admin" || user?.role === "teacher";
  const { current: ay } = useAcademicYear(isSchool);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <>
      {/* Top bar */}
      <div className="ep-glass sticky top-0 z-40 flex items-center justify-between px-5 py-3 border-b border-slate-800/60">
        <div className="flex items-center gap-3">
          {school?.logo_url ? (
            <>
              <img src={fileUrl(school.logo_url)} alt="" className="h-9 w-9 rounded-xl object-cover ring-1 ring-slate-700" data-testid="topbar-logo" />
              <div>
                <p className="text-sm font-bold font-head text-white leading-tight">{school.name}</p>
                <p className="text-[11px] text-slate-400 leading-tight">Powered by EduPulse</p>
              </div>
            </>
          ) : (
            <>
              <BrandMark size={24} onDark />
              <span className="hidden h-6 w-px bg-slate-700 sm:block" />
              <p className="hidden text-sm font-medium leading-tight text-slate-300 sm:block">
                {user?.role === "super_admin" ? "Super Admin Console" : "School Panel"}
              </p>
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isSchool && ay && (
            <NavLink
              to="/school/academic-year"
              className="hidden items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs font-semibold text-slate-200 transition hover:border-indigo-500 hover:text-white sm:inline-flex"
              data-testid="ay-pill"
              title="Academic year"
            >
              <CalendarClock size={13} className="text-indigo-400" /> {ay}
            </NavLink>
          )}
          <div className="hidden sm:block text-right">
            <p className="text-xs font-semibold text-white">{user?.name}</p>
            <p className="text-[11px] text-slate-400 capitalize">{user?.role?.replace("_", " ")}</p>
          </div>
          <button
            onClick={handleLogout}
            className="ep-btn-ghost ep-btn"
            data-testid="logout-btn"
          >
            <LogOut size={16} /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      {/* Floating magic dock */}
      <motion.nav
        initial={{ y: 40, x: "-50%", opacity: 0 }}
        animate={{ y: 0, x: "-50%", opacity: 1 }}
        transition={{ type: "spring", damping: 20, stiffness: 200, delay: 0.15 }}
        className="fixed bottom-5 left-1/2 z-50"
      >
        <div className="ep-glass flex items-center gap-1 rounded-2xl p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-x-auto max-w-[95vw]">
          {items.filter((it) => !it.roles || it.roles.includes(user?.role)).map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.key}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 whitespace-nowrap rounded-xl px-3.5 py-2 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/30"
                    : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
                }`
              }
            >
              <it.icon size={17} />
              <span className="hidden md:inline">{it.label}</span>
            </NavLink>
          ))}
        </div>
      </motion.nav>
    </>
  );
}
